import { Loading, PortalLayout } from "@repo/ui";
import { ReactNode, useContext, useEffect, useState } from "react";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";

import { useRepQueries } from "./api/repApi";
import { OnboardingSelectors, RepDisplayName } from "./components/header";
import { usePages, useStates } from "./components/header/hooks";
import { SearchButton } from "./components/header/searchDialog";
import { AgentPortalLoginPage } from "./components/loginPage";
import { RepsPage } from "./components/repsGridPage/components";
import { RepProvider, useRepContext } from "./contexts/editableItemContext";
import { EntityIdProvider } from "./contexts/entityIdContext";
import { OnboardingProvider } from "./contexts/onboardingContext";
import { PendingChangesProvider, usePendingChangesContext } from "./contexts/pendingChangesContext";
import { SegmentProvider } from "./contexts/segmentContext";
import { UserContext } from "./contexts/userContext";
import { useRepId, useSelectedTab } from "./helpers/urlHooks";
import { RepStatusEnum } from "./models/rep";
import { tabs } from "./string";
import BackgroundChecks from "./tabs/bgChecks";
import Complaints from "./tabs/complaints";
import Disciplines from "./tabs/disciplines";
import Documentation from "./tabs/documentation";
import DrugTests from "./tabs/drugTests";
import EnrollmentValidation from "./tabs/enrollmentValidation";
import FieldAudits from "./tabs/fieldAudits";
import IdValidationTab from "./tabs/idValidation";
import OnboardingAudits from "./tabs/onboardingAudits";
import OnboardingStates from "./tabs/onboardingSessions";
import PersonalInfo from "./tabs/personalInfo";
import Summary from "./tabs/summary";
import TrainerCertification from "./tabs/trainerCert";
import Training from "./tabs/training";

export function RepPortal() {
	const defaultRoute = "/reps";
	const { loggedIn, loading } = useContext(UserContext);

	if (!loggedIn) {
		return (
			<Loading loading={loading} fullHeight>
				<AgentPortalLoginPage />
			</Loading>
		);
	}

	return (
		<PendingChangesProvider>
			<EntityIdProvider entityId={undefined}>
				<RepProvider>
					<SegmentProvider>
						<OnboardingProvider>
							<BrowserRouter>
								<Switch>
									<Route exact path="/">
										<RepPortalInternal />
									</Route>
									<Route path="/reps">
										<RepsPage />
									</Route>
									<Route path="/AgentPortal/:repId?/:tab?/:entityId?">
										<RoutedPortal />
									</Route>
									<Route path="*" render={() => <Redirect to={defaultRoute} />} />
								</Switch>
							</BrowserRouter>
						</OnboardingProvider>
					</SegmentProvider>
				</RepProvider>
			</EntityIdProvider>
		</PendingChangesProvider>
	);
}

function RoutedPortal() {
	const repId = useRepId();
	const { overwriteItem } = useRepContext();
	const { useRepInfoQuery } = useRepQueries();
	const [repLoaded, setRepLoaded] = useState(false);
	const { data, loaded } = useRepInfoQuery(repId);

	useEffect(() => {
		if (data && loaded) {
			overwriteItem(data);
			setRepLoaded(true);
		}
	}, [data, loaded]);

	return (
		<Loading loading={!repLoaded || !loaded} fullHeight>
			<RepPortalInternal />
		</Loading>
	);
}

export function RepPortalInternal({ editOnly }: { editOnly?: boolean }) {
	const { repId, rep, repLoaded, overwriteItem } = useRepContext();
	const selectedTab = useSelectedTab();
	const { useRepInfoQuery } = useRepQueries();
	const { data: repData, loaded: repDataLoaded } = useRepInfoQuery(repId);
	const pages = usePages();
	const states = useStates();
	const { pendingChanges } = usePendingChangesContext();
	const defaultTab = selectedTab || (!repId ? tabs.personalInfo : tabs.summary);

	useEffect(() => {
		if (repId && repDataLoaded && repData) {
			overwriteItem(repData);
		}
	}, [repId, repDataLoaded, repData, overwriteItem]);

	const loaded = !repId || repLoaded || repDataLoaded;

	return (
		<Loading loading={!loaded} fullHeight>
			<PortalLayout
				title={<RepDisplayName rep={rep} />}
				showSidebar={!editOnly && !!repId}
				defaultActiveState={rep.statusEnum === RepStatusEnum.Active ? "sales" : "onboarding"}
				defaultPage={defaultTab}
				pages={pages}
				states={states}
				entityId={repId?.toString()}
				pendingChanges={pendingChanges}
				sidebarAction={editOnly ? null : <OnboardingSelectors />}
				headerAction={editOnly ? null : <SearchButton />}
			>
				{renderTabPanels(editOnly)}
			</PortalLayout>
		</Loading>
	);

	function renderTabPanels(editOnly?: boolean) {
		if (editOnly) {
			return (
				<APTabPanel tabName={tabs.personalInfo}>
					<PersonalInfo />
				</APTabPanel>
			);
		}

		return (
			<>
				<APTabPanel tabName={tabs.summary}>
					<Summary />
				</APTabPanel>
				<APTabPanel tabName={tabs.personalInfo}>
					<PersonalInfo />
				</APTabPanel>
				<APTabPanel tabName={tabs.bgChecks}>
					<BackgroundChecks />
				</APTabPanel>
				<APTabPanel tabName={tabs.drugTests}>
					<DrugTests />
				</APTabPanel>
				<APTabPanel tabName={tabs.training}>
					<Training />
				</APTabPanel>
				<APTabPanel tabName={tabs.trainerCert}>
					<TrainerCertification />
				</APTabPanel>
				<APTabPanel tabName={tabs.documentation}>
					<Documentation />
				</APTabPanel>
				<APTabPanel tabName={tabs.complaints}>
					<Complaints />
				</APTabPanel>
				<APTabPanel tabName={tabs.onboardingStates}>
					<OnboardingStates />
				</APTabPanel>
				<APTabPanel tabName={tabs.eves}>
					<EnrollmentValidation />
				</APTabPanel>
				<APTabPanel tabName={tabs.idValidation}>
					<IdValidationTab />
				</APTabPanel>
				<APTabPanel tabName={tabs.repDisciplines}>
					<Disciplines />
				</APTabPanel>
				<APTabPanel tabName={tabs.onboardingAudits}>
					<OnboardingAudits />
				</APTabPanel>
				<APTabPanel tabName={tabs.fieldAudits}>
					<FieldAudits />
				</APTabPanel>
				{!selectedTab || Object.values(tabs).includes(selectedTab as any) ? null : "No page found"}
			</>
		);
	}
}

interface APTabPanelProps {
	children: ReactNode;
	tabName: string;
}

function APTabPanel(props: APTabPanelProps) {
	const { repId } = useRepContext();
	const selectedTab = useSelectedTab();

	const newRep = !repId;
	const tab = selectedTab || (newRep ? tabs.personalInfo : tabs.summary);
	const visible = props.tabName === tab;
	const [shownOnce, setShownOnce] = useState(visible);

	useEffect(() => {
		setShownOnce(visible);
	}, [repId]); //If change to different rep, drop the tabs

	useEffect(() => {
		if (visible) setShownOnce(true);
	}, [visible]);

	if (!shownOnce) return null;

	if (!visible) return null;

	return <div className="p-2">{props.children}</div>;
}
